import { useContext, useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import {
  toProblemDetail,
  updateRecord
} from '../../../services/syntiro_honey/data_ingestion.service';
import { EsgRecord, EsgRecordUpdateResponse } from '../../../types/syntiro_honey';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { aliasOutcomeFeedback } from './alias_feedback';

/** Compact enough to sit inside a grid cell beside the mapping chip. */
const CONFIRM_BUTTON_SX = {
  textTransform: 'none',
  py: 0,
  px: 1,
  minWidth: 0,
  fontSize: '0.75rem',
  whiteSpace: 'nowrap'
} as const;

interface ConfirmMappingButtonProps {
  /** The record whose SUGGESTED metric the reviewer confirms. */
  record: EsgRecord;
  /**
   * Called after a successful confirmation so the parent refreshes the list. Receives
   * the updated record so the parent can act on WHAT was confirmed (e.g. offer to
   * apply the same metric to the other rows sharing its label).
   */
  onConfirmed: (updated: EsgRecordUpdateResponse) => void;
}

/**
 * One-click confirmation of an AI-suggested metric mapping. Confirming re-submits
 * the record's own metric as the reviewer's explicit choice, which (a) promotes the
 * mapping to an exact, human-verified one, and (b) teaches the system: the backend
 * stores the confirmation as a per-client alias, so the same source label maps
 * automatically on every future upload. This button is the reviewer-facing end of
 * that learning loop — one click here saves a review step on every next file.
 * @param {EsgRecord} record The record whose suggested metric is confirmed.
 * @param {() => void} onConfirmed Refresh callback after success.
 * @returns {JSX.Element | null}
 */
export default function ConfirmMappingButton({ record, onConfirmed }: ConfirmMappingButtonProps) {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Only a SUGGESTED mapping needs confirming; exact/unmapped rows render nothing.
  // (Captured into a local so the narrowing survives into the click handler.)
  const suggestedMetricId = record.metricId;
  // An APPROVED record is locked (immutable) — confirming it would be rejected
  // with a 409, so the button must not appear even when its mapping is SUGGESTED.
  if (
    record.status === 'APPROVED' ||
    record.mappingStatus !== 'SUGGESTED' ||
    suggestedMetricId === null
  ) {
    return null;
  }

  const handleConfirm = async (event: React.MouseEvent): Promise<void> => {
    // The button lives inside a clickable grid row — never open the drawer too.
    event.stopPropagation();
    setIsBusy(true);
    setErrorMessage(null);
    try {
      const updated = await updateRecord(record.id, { metricId: suggestedMetricId });
      // Make the learning loop visible: this click also taught the per-client alias.
      setFeedbackData(aliasOutcomeFeedback(updated));
      onConfirmed(updated);
    } catch (error) {
      setErrorMessage(toProblemDetail(error).detail ?? 'Confirming the mapping failed.');
    } finally {
      setIsBusy(false);
    }
  };

  if (errorMessage !== null) {
    return (
      <Tooltip title={`${errorMessage} Click to retry.`}>
        <Button
          size="small"
          variant="outlined"
          color="warning"
          startIcon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
          onClick={handleConfirm}
          aria-label="Retry confirming metric"
          sx={CONFIRM_BUTTON_SX}
        >
          Retry
        </Button>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Confirm this metric — it will then map automatically for this client on future uploads">
      {/* span keeps the tooltip working while the button is disabled */}
      <span>
        {/* A LABELLED button, not a bare tick. This is the most repeated act in the
            triage flow, and as a green check icon it was both undiscoverable (hover
            only) and indistinguishable from the green check ICONS that mean "value
            trusted" one column over — a verb dressed as a noun. Text makes it read
            as an action; outlined keeps it quieter than the bulk-bar primaries.

            Stays MOUNTED while busy (disabled, spinner inside): unmounting the
            button would drop keyboard focus to <body> on every confirm — a real
            cost in the repetitive-triage flow this control exists for. */}
        <Button
          size="small"
          variant="outlined"
          disabled={isBusy}
          onClick={handleConfirm}
          aria-label="Confirm suggested metric"
          aria-busy={isBusy}
          startIcon={
            isBusy ? <CircularProgress size={14} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />
          }
          sx={CONFIRM_BUTTON_SX}
        >
          Confirm
        </Button>
      </span>
    </Tooltip>
  );
}
